// frontend/src/pages/PayrollDrafts/index.tsx
// Thin orchestrator: state, data wiring, derived values.
// Delegates heavy JSX to sub-components, mutations to usePayRunMutations,
// and standard states (loading/error/empty) + CSV export to shared infra.

import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Play, FileText } from "lucide-react";

import type {
  PayRun,
  TabType,
  SortType,
  PeriodType,
} from "./types";
import { fmtDate, fmtMoney } from "./helpers";
import {
  FONT,
  C,
  outlineBtnStyle,
  footerBtnStyle,
  thStyle,
  tabBtnStyle,
} from "./styles";
import { usePayRuns } from "./usePayRuns";
import { usePayRunMutations } from "./useMutations";

import PayRunRow from "./components/PayRunRow";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import FilterMenu from "./components/FilterMenu";

import LoadingState from "../../components/common/LoadingState";
import ErrorState from "../../components/common/ErrorState";
import EmptyState from "../../components/common/EmptyState";
import { exportToCsv } from "../../utils/csv";

// Re-export shared types so other pages can import them from here if they need to.
export type { PayRun, PayRunStatus } from "./types";

export default function PayrollDrafts(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── State ───────────────────────────────────────────────────────────────
  const [runs, setRuns] = useState<PayRun[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabType>("all");
  const [sort, setSort] = useState<SortType>("newest");
  const [period, setPeriod] = useState<PeriodType>("all");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PayRun | null>(null);
  const [company] = useState<string>(localStorage.getItem("company_name") || "");

  // ── Mutations ───────────────────────────────────────────────────────────
  const { deletePayRun, deleting } = usePayRunMutations();

  // ── Effects ─────────────────────────────────────────────────────────────
  useEffect(function () {
    function onClick(e: MouseEvent) {
      if (!(e.target as HTMLElement)?.closest?.(".row-kebab")) setOpenMenuId(null);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpenMenuId(null);
        setConfirmDelete(null);
      }
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return function () {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // ── Data ────────────────────────────────────────────────────────────────
  const { data: runsData, isLoading: runsLoading, error: runsError } = usePayRuns();

  useEffect(function () {
    if (runsData) setRuns(runsData);
    setLoading(runsLoading);
    if (runsError) setError((runsError as Error).message);
  }, [runsData, runsLoading, runsError]);

  function fetchRuns() {
    queryClient.invalidateQueries({ queryKey: ["payroll-runs"] });
  }

  // ── Mutation side-effect wrapper ────────────────────────────────────────
  async function handleDelete(runId: string): Promise<void> {
    const result = await deletePayRun(runId);
    if (!result.success) {
      alert("Could not delete: " + result.error);
      return;
    }
    setConfirmDelete(null);
    fetchRuns();
  }

  // ── Derived values ──────────────────────────────────────────────────────
  const counts = useMemo(function () {
    return {
      all: runs.length,
      drafts: runs.filter((r) => r.status === "draft").length,
      finalized: runs.filter((r) => r.status === "finalized").length,
    };
  }, [runs]);

  const filtered = useMemo(function () {
    let list = runs.slice();
    if (tab === "drafts") list = list.filter((r) => r.status === "draft");
    else if (tab === "finalized") list = list.filter((r) => r.status === "finalized");
    if (period !== "all") {
      const now = new Date();
      let cutoff: Date | null = null;
      if (period === "this-year") cutoff = new Date(now.getFullYear(), 0, 1);
      else if (period === "last-quarter") { cutoff = new Date(now); cutoff.setMonth(cutoff.getMonth() - 3); }
      else if (period === "this-month") cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
      if (cutoff) list = list.filter((r) => r.pay_date != null && new Date(r.pay_date) >= cutoff!);
    }
    if (sort === "newest") list.sort((a, b) => new Date(b.pay_date || 0).getTime() - new Date(a.pay_date || 0).getTime());
    else if (sort === "oldest") list.sort((a, b) => new Date(a.pay_date || 0).getTime() - new Date(b.pay_date || 0).getTime());
    else if (sort === "net-high") list.sort((a, b) => Number(b.total_net || 0) - Number(a.total_net || 0));
    else if (sort === "net-low") list.sort((a, b) => Number(a.total_net || 0) - Number(b.total_net || 0));
    return list;
  }, [runs, tab, sort, period]);

  const sectionTitle =
    tab === "drafts" ? "Drafts to continue" :
    tab === "finalized" ? "Finalized pay runs" :
    "Pay run history";

  function handleExportCSV(): void {
    const headers = ["Pay period", "Pay date", "Employees", "Net total", "Status"];
    const rows = filtered.map((r) => [
      fmtDate(r.pay_period_start) + " to " + fmtDate(r.pay_period_end),
      fmtDate(r.pay_date),
      r.employee_count || 0,
      fmtMoney(r.total_net),
      r.status,
    ]);
    exportToCsv("pay_runs_" + new Date().toISOString().slice(0, 10), headers, rows);
  }

  function openRun(r: PayRun): void {
    // Draft: goes to Run Payroll (edit mode). Finalized: goes to Done page.
    if (r.status === "draft") navigate("/payroll/run/" + r.id);
    else navigate("/payroll/run/" + r.id + "/done");
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ background: C.page, minHeight: "100vh", fontFamily: FONT, color: C.ink, padding: "28px 32px 100px", boxSizing: "border-box" }}>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: C.ink, marginBottom: 14 }}>
        <span style={{ fontWeight: 600, opacity: 0.7, cursor: "pointer" }} onClick={() => navigate("/payroll/overview")}>Payroll</span>
        <span style={{ opacity: 0.4 }}>/</span>
        <span style={{ fontWeight: 700 }}>Pay runs</span>
      </div>

      {/* Title + Run payroll button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 34, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em" }}>Pay runs</h1>
          <div style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginTop: 4 }}>
            All payroll runs{company ? " for " + company : ""}.
          </div>
        </div>
        <button
          onClick={() => navigate("/payroll/run")}
          style={{
            padding: "12px 20px",
            background: C.brand,
            border: "none",
            borderRadius: 10,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontFamily: FONT,
            boxShadow: "0 1px 2px rgba(21,160,140,0.3)",
          }}
        >
          <Play size={16} /> Run payroll
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid " + C.line }}>
        <button onClick={() => setTab("all")} style={tabBtnStyle(tab === "all")}>All <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>{counts.all}</span></button>
        <button onClick={() => setTab("drafts")} style={tabBtnStyle(tab === "drafts")}>Drafts <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>{counts.drafts}</span></button>
        <button onClick={() => setTab("finalized")} style={tabBtnStyle(tab === "finalized")}>Finalized <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>{counts.finalized}</span></button>
      </div>

      {/* Section header + filter + export */}
      <div style={{ paddingBottom: 8, borderBottom: "1.5px solid " + C.ink, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink }}>{sectionTitle}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FilterMenu
            sort={sort}
            onSortChange={setSort}
            period={period}
            onPeriodChange={setPeriod}
          />
          <button onClick={handleExportCSV} style={outlineBtnStyle}>Export CSV</button>
        </div>
      </div>

      {/* States */}
      {loading && <LoadingState message="Loading pay runs..." color={C.ink} />}

      {error && !loading && <ErrorState message={error} onRetry={fetchRuns} />}

      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          icon={<FileText size={48} />}
          title={
            tab === "drafts" ? "No drafts" :
            tab === "finalized" ? "No finalized runs yet" :
            "No pay runs yet"
          }
          description={
            tab === "drafts" ? "All your pay runs have been finalized." :
            tab === "finalized" ? "Submit a pay run to see it here." :
            "Create your first pay run to calculate gross pay, taxes, and net pay in one cycle."
          }
          action={
            tab === "all"
              ? {
                  label: "Run your first payroll",
                  icon: <Play size={16} />,
                  onClick: () => navigate("/payroll/run"),
                }
              : undefined
          }
          fontFamily={FONT}
          color={C.ink}
        />
      )}

      {/* Table */}
      {!loading && !error && filtered.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr>
              <th style={thStyle}>PAY PERIOD</th>
              <th style={thStyle}>PAY DATE</th>
              <th style={{ ...thStyle, textAlign: "right" }}>EMPLOYEES</th>
              <th style={{ ...thStyle, textAlign: "right" }}>NET TOTAL</th>
              <th style={thStyle}>STATUS</th>
              <th style={{ ...thStyle, textAlign: "right", width: 60 }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <PayRunRow
                key={r.id}
                payRun={r}
                isKebabOpen={openMenuId === r.id}
                onOpenRun={() => openRun(r)}
                onToggleKebab={() => setOpenMenuId(openMenuId === r.id ? null : r.id)}
                onCloseKebab={() => setOpenMenuId(null)}
                onRequestDelete={() => setConfirmDelete(r)}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <DeleteConfirmModal
          target={confirmDelete}
          deleting={deleting}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete.id)}
        />
      )}

      {/* Fixed footer */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: C.card, padding: "16px 32px", borderTop: "1px solid " + C.line, boxShadow: "0 -4px 12px rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 100 }}>
        <button onClick={() => navigate("/payroll/overview")} style={footerBtnStyle}>&larr; Back to Payroll</button>
        <button onClick={handleExportCSV} style={footerBtnStyle}>Export CSV</button>
      </div>
    </div>
  );
}